import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Lead } from '@se/core/models';
import { ApiService } from '../_helpers/api.service';
import { REST_BASE_URL, REST_URLS, WEB_STORAGE_KEYS, countryPhoneExtensionMap } from '@se/core/constants';
import { map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';
import { SgSessionTokenService } from './sg-session-token.service';

@Injectable({
  providedIn: 'root'
})
export class LeadsService {
  constructor(
    private _apiService: ApiService,
    private http: HttpClient,
    private sgSessionTokenService: SgSessionTokenService,
  ) { }
  public isV3 = false;
  public isV4 = false;
  public isNLUser = false;

  private getAuthHeaders(): HttpHeaders {
    const requestId = `slf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const sgSessionToken = this.sgSessionTokenService.getToken();

    let headers = new HttpHeaders({
      Accept: 'application/json',
      'X-App-ID': environment.appName || 'enphase-system-estimator',
      'X-Correlation-Id': requestId,
    });

    if (sgSessionToken) {
      headers = headers.set('Authorization', `Bearer ${sgSessionToken}`);
    }

    return headers;
  }

  private encodeUtf8ToBase64(payload: Record<string, any>): string {
    return btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
  }

  private buildSlfPayload(lead: Lead): Record<string, any> {
    const address = (lead && lead.address) || ({} as any);
    const locale = String((lead && lead.locale) || 'en-us').toLowerCase();
    const localeParts = locale.split('-');
    const lang = (localeParts[0] || 'en').toUpperCase();
    const countryFromLocale = (localeParts[1] || 'us').toUpperCase();
    const country = String((address && address.country) || countryFromLocale || 'US').toUpperCase();
    const campaignCountry = String(country || 'US').toLowerCase();
    const rawPhone = String((lead && lead.phone) || '').trim();
    const digitsOnlyPhone = rawPhone.replace(/\D/g, '');
    const dialCode = String((countryPhoneExtensionMap as any)[country] || '').replace(/\D/g, '');
    const normalizedPhone = digitsOnlyPhone
      ? (dialCode
        ? ((rawPhone.startsWith('+') || digitsOnlyPhone.startsWith(dialCode))
          ? `+${digitsOnlyPhone}`
          : `+${dialCode}${digitsOnlyPhone}`)
        : digitsOnlyPhone)
      : '';
    const utmParams = (lead && lead.utm_params) || ({} as any);
    const selectedProductInterest = String((lead && lead.product_type) || 'solarstorage');
    const interestedInByProduct: Record<string, string> = {
      solar: 'Solar',
      storage: 'Storage',
      solarstorage: 'Solar & Storage',
      upgrade: 'Solar & Storage',
    };
    const normalizedInterestedIn =
      country === 'NL'
        ? 'Battery only'
        : (
          interestedInByProduct[selectedProductInterest] ||
          (lead && lead.requirement) ||
          'Solar & Storage'
        );
    const normalizedUtmSource = country === 'NL'
      ? 'NL system estimator'
      : (utmParams && utmParams.utm_source) || '';
    const normalizedProductInterest = country === 'NL' ? 'Battery only' : selectedProductInterest;
    const selectedCustomerSiteId =
      (lead && lead.existing_system && (lead.existing_system as any).site_id) ||
      ((lead as any) && (lead as any).site_id) ||
      ((lead as any) && (lead as any).siteId) ||
      localStorage.getItem(WEB_STORAGE_KEYS.site_id) ||
      '';
    const normalizedEnphLeadId = selectedCustomerSiteId
      ? String(selectedCustomerSiteId)
      : String((lead && lead.lead_id) || '');
        
    return {
      landing_page: lead && lead.page_url ? lead.page_url : window.location.href,
      first_name: (lead && lead.first_name) || '',
      last_name: (lead && lead.last_name) || '',
      email: (lead && lead.email) || '',
      phone_number: normalizedPhone,
      country,
      lang,
      street: (address && address.address1) || '',
      city: (address && address.city) || '',
      zip: (address && address.zip) || (lead && lead.zip) || '',
      state: (address && address.state) || '',
      user_type: (lead && lead.customer_type ? lead.customer_type : 'new'),
      ref_code: (lead && (lead as any).referralCode) || '',
      campaign: `${campaignCountry}-battery`,
      lead_type: '3',
      lead_type_text: '',
      enph_lead_id: normalizedEnphLeadId,
      house: (lead && lead.own_house != null) ? String(lead.own_house) : '',
      nl_ho_event_26_attendance: '',
      message: (lead && lead.additional_info) || '',
      additional_phone: '',
      utm_source: normalizedUtmSource,
      utm_medium: (utmParams && utmParams.utm_medium) || '',
      utm_content: (utmParams && utmParams.utm_content) || '',
      utm_campaign: (utmParams && utmParams.utm_campaign) || '',
      utm_term: (utmParams && utmParams.utm_term) || '',
      browser_info: navigator.userAgent || '',
      product_interest: normalizedProductInterest,
      type_of_roof: '',
      sunlight_on_roof: (lead && lead.slf_shading_info) || '',
      timeline: (lead && lead.installation_timeline) || '',
      interested_in: normalizedInterestedIn,
      SLF_Campaign_Lead_Type: '',
      financing: (lead && lead.selected_finance_option === 'loan') ? 'Yes' : 'No',
    };
  }

  insertLead(lead: Lead): Observable<Lead> {
    // poupluate SLF fields for lead
    const xxTrustedFormCertUrl = document.getElementById('xxTrustedFormCertUrl_0') as HTMLInputElement;
    // const uleadid = document.getElementById('leadid_token') as HTMLInputElement;
    const iq_meter_collar_recommendation = JSON.parse(localStorage.getItem('iqMeterCollar') || 'false');
    const meter_collar_bh_wh_meter = JSON.parse(localStorage.getItem('meterCollerWH') || 'false');
    // lead.universal_leadid = (uleadid)?uleadid.value:'';
    lead.landing_page = "form-estimator";
    lead.lp_type =  "ussp-multi-form";
    lead.ownership_status = 'on';
    lead.enphase_branded = '1';
    lead.sandbox = '1';
    (lead as any).iq_meter_collar_recommendation = iq_meter_collar_recommendation;
    (lead as any).meter_collar_bh_wh_meter = meter_collar_bh_wh_meter;
    lead.xxTrustedFormCertUrl = (xxTrustedFormCertUrl)?xxTrustedFormCertUrl.value:'';

    if (this.isV4) {
      const slfPayload = this.buildSlfPayload(lead);
      const encodedPayload = this.encodeUtf8ToBase64(slfPayload);
      return this.http.post(
        `${environment.REST_URL.SG_API_WRAPPER_URL}${REST_URLS.POST.SG_SLF_LEADS}`,
        { payload: encodedPayload },
        { headers: this.getAuthHeaders() },
      ) as Observable<Lead>;
    }

    return this._apiService.post(REST_BASE_URL.B2C, this._apiService.composeUrl([REST_URLS.POST.LEADS]), lead);
  }
  updateExistingLead(lead: Lead, lead_id): Observable<Lead> {
    return this._apiService.put(REST_BASE_URL.B2C, this._apiService.composeUrl([REST_URLS.PUT.LEADS, lead_id]), lead);
  }
  updateLead(leadId, newLead, main_panel_bus_rating, main_breaker_rating): Observable<Lead> {
    return this._apiService.put(REST_BASE_URL.B2C, this._apiService.composeUrl([REST_URLS.POST.LEADS, leadId, 'new_system']), newLead, this._apiService.composeUrlParams(['main_panel_bus_rating', 'main_breaker_rating'], [main_panel_bus_rating, main_breaker_rating]));
  }
  updateLeadInfo(leadId, data): Observable<Lead> {
    return this._apiService.put(REST_BASE_URL.B2C, this._apiService.composeUrl([REST_URLS.POST.LEADS, leadId, 'info']), data);
  }
  get(siteId: number): Observable<Lead> {
    const iqMeterCollar = JSON.parse(localStorage.getItem('iqMeterCollar') || 'false');
    const meterCollerWH = JSON.parse(localStorage.getItem('meterCollerWH') || 'false');
    return this._apiService.get(REST_BASE_URL.B2C, this._apiService.composeUrl([REST_URLS.GET.SEARCH_LEADS]), 
    this._apiService.composeUrlParams(['site_id'],[siteId])).pipe(
      map(result => {
        this.isV3 = result.se_version === 'v3';
        return new Lead(result);
      })
    );
  }
  getUploadImageLink(fileName, httpMethod): Observable<any> {
    return this._apiService.get(REST_BASE_URL.B2C, this._apiService.composeUrl([REST_URLS.GET.QUOTE_UPLOAD_IMAGES]), this._apiService.composeUrlParams(['file_name', 'http_method'], [fileName, httpMethod]))
  }
}
